import { useQuery } from '@tanstack/react-query';
import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { reportsApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  SectionHeader,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * Dashboard — executive overview, blueprint §14.
 *
 * Data composition:
 *   - /reports/dashboard   → today totals + outstanding AR/AP + low stock count
 *   - salesDaily(-14d)     → trend chart + tender breakdown + revenue delta
 *   - salesByCategory(-30d)→ top categories
 *   - apAging / arAging    → aging bucket summaries for alert cards
 *
 * All charts are inline SVG (no chart library dependency). This keeps
 * the bundle small and the aesthetic tightly controlled.
 */
export function DashboardPage() {
  // --- Primary dashboard summary ---
  const summaryQ = useQuery({
    queryKey: ['dashboard'],
    queryFn: reportsApi.dashboard,
    refetchInterval: 30_000,
  });

  // --- Last 14 days trend ---
  const range14 = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 13);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  }, []);

  const trendQ = useQuery({
    queryKey: ['dashboard-trend', range14.start, range14.end],
    queryFn: () => reportsApi.salesDaily({ startDate: range14.start, endDate: range14.end }),
  });

  // --- Top categories last 30 days ---
  const range30 = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  }, []);

  const categoryQ = useQuery({
    queryKey: ['dashboard-categories', range30.start, range30.end],
    queryFn: () => reportsApi.salesByCategory({ startDate: range30.start, endDate: range30.end }),
  });

  // --- AR / AP aging ---
  const arAgingQ = useQuery({
    queryKey: ['dashboard-ar-aging'],
    queryFn: () => reportsApi.financeArAging(),
  });
  const apAgingQ = useQuery({
    queryKey: ['dashboard-ap-aging'],
    queryFn: () => reportsApi.financeApAging(),
  });

  // --- Derived metrics ---
  const derived = useMemo(() => {
    if (!trendQ.data) return null;
    const daily = (trendQ.data.daily ?? []) as any[];
    if (!daily.length) return { totalRevenue: 0, avgDaily: 0, recent: 0, prior: 0, deltaPct: null };
    const totalRevenue = daily.reduce((a, d) => a + (d.grandTotal ?? 0), 0);
    const avgDaily = daily.length ? totalRevenue / daily.length : 0;
    // Compare last 7 days vs previous 7 days for delta
    const n = daily.length;
    const recent = daily.slice(Math.max(0, n - 7)).reduce((a, d) => a + (d.grandTotal ?? 0), 0);
    const prior  = daily.slice(Math.max(0, n - 14), Math.max(0, n - 7)).reduce((a, d) => a + (d.grandTotal ?? 0), 0);
    const deltaPct = prior > 0 ? ((recent - prior) / prior) * 100 : null;
    return { totalRevenue, avgDaily, recent, prior, deltaPct };
  }, [trendQ.data]);

  if (summaryQ.isLoading && trendQ.isLoading) {
    return (
      <div className="px-8 py-10 max-w-7xl mx-auto">
        <PageHeader eyebrow="Overview" title="Dashboard" />
        <LoadingState rows={8} />
      </div>
    );
  }

  if (summaryQ.error) {
    return (
      <div className="px-8 py-10 max-w-7xl mx-auto">
        <PageHeader eyebrow="Overview" title="Dashboard" />
        <ErrorState error={summaryQ.error} />
      </div>
    );
  }

  const s = summaryQ.data;
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow={today}
        title="Good day — here's what's happening"
        subtitle="Key metrics, trends, and alerts across your retail operations."
        actions={
          <>
            <Link to="/reports" className="btn-secondary">
              <ChartIcon /> View all reports
            </Link>
            <Link to="/pos" className="btn-primary">
              <CartIcon /> Open POS
            </Link>
          </>
        }
      />

      {/* ============================================================
          KPI hero strip — 4 primary metrics
          ============================================================ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiTile
          label="Sales today"
          value={fmtMoney(s?.today?.salesTotal ?? 0)}
          meta={`${s?.today?.salesCount ?? 0} transactions`}
          trend={derived?.deltaPct != null
            ? { value: `${derived.deltaPct >= 0 ? '+' : ''}${derived.deltaPct.toFixed(1)}% vs last week`,
                direction: derived.deltaPct >= 0 ? 'up' : 'down' }
            : undefined}
          accent="sage"
          icon={<CartIcon />}
        />
        <KpiTile
          label="Avg daily revenue"
          value={fmtMoney(derived?.avgDaily ?? 0)}
          meta="Last 14 days"
          accent="info"
          icon={<ChartIcon />}
        />
        <KpiTile
          label="AR outstanding"
          value={fmtMoney(s?.arOutstanding ?? 0)}
          meta="Customer invoices unpaid"
          accent={s?.arOutstanding > 0 ? 'amber' : 'sage'}
          icon={<ClockIcon />}
        />
        <KpiTile
          label="AP outstanding"
          value={fmtMoney(s?.apOutstanding ?? 0)}
          meta="Vendor invoices unpaid"
          accent={s?.apOutstanding > 0 ? 'danger' : 'default'}
          icon={<WalletIcon />}
        />
      </div>

      {/* ============================================================
          Trend chart — full width
          ============================================================ */}
      <div className="card p-6 mb-6">
        <SectionHeader
          title="Revenue — last 14 days"
          description={derived
            ? `${fmtMoney(derived.totalRevenue)} total · ${fmtMoney(derived.avgDaily)} daily average`
            : undefined}
          actions={
            <Link to="/reports/sales-inventory" className="text-sm font-medium text-ink-600 hover:text-ink-900 transition-colors">
              View details →
            </Link>
          }
        />
        {trendQ.isLoading ? (
          <LoadingState rows={3} />
        ) : trendQ.error ? (
          <ErrorState error={trendQ.error} />
        ) : trendQ.data?.daily?.length ? (
          <RevenueChart daily={trendQ.data.daily} />
        ) : (
          <EmptyState message="No sales data yet" hint="Complete your first POS sale to see trends." />
        )}
      </div>

      {/* ============================================================
          Middle row — tenders + categories
          ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card p-6">
          <SectionHeader title="Payment methods" description="Last 14 days" />
          {trendQ.data?.tenders?.length ? (
            <TenderBreakdown tenders={trendQ.data.tenders} />
          ) : (
            <EmptyState message="No payments yet" />
          )}
        </div>

        <div className="card p-6">
          <SectionHeader
            title="Top categories"
            description="Last 30 days"
            actions={
              <Link to="/reports/sales-inventory" className="text-sm font-medium text-ink-600 hover:text-ink-900">
                More →
              </Link>
            }
          />
          {categoryQ.isLoading ? (
            <LoadingState rows={4} />
          ) : categoryQ.data?.rows?.length ? (
            <CategoryBreakdown rows={categoryQ.data.rows.slice(0, 5)} />
          ) : (
            <EmptyState message="No sales data" />
          )}
        </div>
      </div>

      {/* ============================================================
          Bottom row — 3 alert/watch columns
          ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AlertCard
          icon={<AlertIcon />}
          title="Low stock"
          value={s?.stockLowCount ?? 0}
          description={s?.stockLowCount > 0
            ? `${s.stockLowCount} products at or below 10 units`
            : 'All stock levels are healthy'}
          tone={s?.stockLowCount > 0 ? 'warning' : 'success'}
          action={s?.stockLowCount > 0
            ? { label: 'View stock', to: '/inventory/stock' }
            : undefined}
        />

        <AlertCard
          icon={<ClockIcon />}
          title="Receivables overdue"
          value={arAgingQ.data ? fmtMoney(arOverdue(arAgingQ.data)) : '—'}
          description={arAgingQ.data ? `${overdueCount(arAgingQ.data)} invoices past due` : 'Loading…'}
          tone={arAgingQ.data && arOverdue(arAgingQ.data) > 0 ? 'danger' : 'success'}
          action={arAgingQ.data && arOverdue(arAgingQ.data) > 0
            ? { label: 'View AR aging', to: '/accounting/ar/aging' }
            : undefined}
        />

        <AlertCard
          icon={<WalletIcon />}
          title="Payables due"
          value={apAgingQ.data ? fmtMoney(apAgingQ.data.totalAmount ?? 0) : '—'}
          description={apAgingQ.data ? `${apAgingQ.data.totalCount ?? 0} vendor invoices outstanding` : 'Loading…'}
          tone="default"
          action={apAgingQ.data && apAgingQ.data.totalCount > 0
            ? { label: 'View AP aging', to: '/accounting/ap/aging' }
            : undefined}
        />
      </div>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================
function fmtInt(n: number) { return n.toLocaleString(); }

function arOverdue(aging: any): number {
  const buckets = aging?.bucketSummary ?? [];
  return buckets.filter((b: any) => b.label !== 'Current').reduce((a: number, b: any) => a + (b.amount ?? 0), 0);
}
function overdueCount(aging: any): number {
  const buckets = aging?.bucketSummary ?? [];
  return buckets.filter((b: any) => b.label !== 'Current').reduce((a: number, b: any) => a + (b.count ?? 0), 0);
}

// ============================================================
// KpiTile — dashboard hero metric
// ============================================================
const TILE_ACCENT = {
  default: { bar: 'bg-ink-300',   icon: 'bg-canvas-subtle text-ink-500',   value: 'text-ink-900' },
  sage:    { bar: 'bg-sage-500',   icon: 'bg-sage-100 text-sage-600',       value: 'text-sage-700' },
  amber:   { bar: 'bg-amber-500',  icon: 'bg-amber-100 text-amber-700',     value: 'text-amber-800' },
  danger:  { bar: 'bg-danger',     icon: 'bg-red-100 text-red-600',         value: 'text-red-700' },
  info:    { bar: 'bg-info',       icon: 'bg-blue-100 text-blue-600',       value: 'text-blue-700' },
};

function KpiTile({
  label, value, meta, trend, accent = 'default', icon,
}: {
  label: string;
  value: string;
  meta?: string;
  trend?: { value: string; direction: 'up' | 'down' };
  accent?: keyof typeof TILE_ACCENT;
  icon?: ReactNode;
}) {
  const a = TILE_ACCENT[accent];
  const trendColor = trend?.direction === 'up' ? 'text-sage-600' : 'text-danger';

  return (
    <div className="card p-5 relative overflow-hidden">
      {/* Vertical accent bar */}
      <div className={`absolute top-0 left-0 bottom-0 w-1 ${a.bar} rounded-l-xl`} />
      <div className="pl-3 flex items-start justify-between gap-2">
        <div className="eyebrow text-ink-500">{label}</div>
        {icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${a.icon}`}>
            {icon}
          </div>
        )}
      </div>
      <div className={`pl-3 mt-3 text-3xl font-bold font-display tracking-tight ${a.value}`}>
        {value}
      </div>
      {meta && <div className="pl-3 mt-1 text-xs text-ink-500">{meta}</div>}
      {trend && (
        <div className={`pl-3 mt-2 inline-flex items-center gap-1 text-xs font-semibold ${trendColor}`}>
          {trend.direction === 'up' ? '↗' : '↘'} {trend.value}
        </div>
      )}
    </div>
  );
}

// ============================================================
// RevenueChart — SVG area chart (no library dep)
// ============================================================
function RevenueChart({ daily }: { daily: any[] }) {
  const W = 800, H = 220;
  const padL = 48, padR = 16, padT = 16, padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const values = daily.map((d) => d.grandTotal ?? 0);
  const maxY = Math.max(...values, 1) * 1.15;
  const stepX = daily.length > 1 ? innerW / (daily.length - 1) : 0;

  const points = daily.map((_, i) => ({
    x: padL + i * stepX,
    y: padT + innerH - (values[i] / maxY) * innerH,
  }));

  // Smooth curve using Catmull-Rom-ish cubic bezier
  const pathD = points.length > 0
    ? 'M ' + points.map((p, i) => {
        if (i === 0) return `${p.x} ${p.y}`;
        const prev = points[i - 1];
        const cx = (prev.x + p.x) / 2;
        return `C ${cx} ${prev.y}, ${cx} ${p.y}, ${p.x} ${p.y}`;
      }).join(' ')
    : '';

  const areaD = points.length > 0
    ? `${pathD} L ${padL + (points.length - 1) * stepX} ${padT + innerH} L ${padL} ${padT + innerH} Z`
    : '';

  // Y-axis gridlines at 0, 50%, 100%
  const gridYs = [0, 0.5, 1].map((f) => padT + innerH - f * innerH);

  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="revenue-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"   stopColor="#008060" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#008060" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Gridlines */}
        {gridYs.map((y, i) => (
          <line key={i} x1={padL} x2={W - padR} y1={y} y2={y}
            stroke="#ECEEF1" strokeDasharray="3 4" />
        ))}

        {/* Y-axis labels */}
        {gridYs.map((y, i) => {
          const val = maxY * (1 - i * 0.5);
          return (
            <text key={i} x={padL - 8} y={y + 4}
              textAnchor="end" fontSize="10" fill="#6B6B61" fontFamily="Inter">
              {fmtMoney(val)}
            </text>
          );
        })}

        {/* Area fill */}
        <path d={areaD} fill="url(#revenue-fill)" />

        {/* Line */}
        <path d={pathD} fill="none" stroke="#008060" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" />

        {/* Data points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill="#FFFFFF" stroke="#008060" strokeWidth="2" />
            {/* Tooltip-ready invisible target (larger hit area) */}
            <circle cx={p.x} cy={p.y} r="14" fill="transparent">
              <title>{`${daily[i].day} · ${fmtMoney(values[i])}`}</title>
            </circle>
          </g>
        ))}

        {/* X-axis labels — every other */}
        {daily.map((d, i) => {
          if (i % 2 !== 0 && i !== daily.length - 1) return null;
          const date = new Date(d.day);
          const label = `${date.getMonth() + 1}/${date.getDate()}`;
          return (
            <text key={i} x={padL + i * stepX} y={H - padB + 16}
              textAnchor="middle" fontSize="10" fill="#6B6B61" fontFamily="Inter">
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ============================================================
// TenderBreakdown — horizontal stacked bar + legend
// ============================================================
function TenderBreakdown({ tenders }: { tenders: any[] }) {
  const total = tenders.reduce((a, t) => a + (t.amount ?? 0), 0) || 1;
  const METHOD_COLOR: Record<string, string> = {
    cash: '#008060',
    card: '#1F1F1B',
    wallet: '#3A6FB5',
    bank_transfer: '#6FAE87',
    gift_card: '#B65E00',
    store_credit: '#96968C',
    check: '#C7C7C0',
  };

  return (
    <div>
      {/* Stacked bar */}
      <div className="h-3 w-full rounded-pill overflow-hidden flex bg-canvas-subtle">
        {tenders.map((t) => (
          <div
            key={t.method}
            style={{
              width: `${(t.amount / total) * 100}%`,
              background: METHOD_COLOR[t.method] ?? '#96968C',
            }}
            title={`${t.method}: ${fmtMoney(t.amount)}`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 space-y-2">
        {tenders.map((t) => (
          <div key={t.method} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: METHOD_COLOR[t.method] ?? '#96968C' }}
              />
              <span className="text-ink-700 capitalize">{t.method.replace('_', ' ')}</span>
              <span className="text-ink-400 text-xs">· {fmtInt(t.count)} txns</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-ink-500 text-xs tabular-nums">
                {((t.amount / total) * 100).toFixed(1)}%
              </span>
              <span className="font-mono font-semibold text-ink-900 tabular-nums">
                {fmtMoney(t.amount)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// CategoryBreakdown — horizontal bar list
// ============================================================
function CategoryBreakdown({ rows }: { rows: any[] }) {
  const max = Math.max(...rows.map((r) => r.revenue ?? 0), 1);
  const COLORS = ['bg-sage-500', 'bg-sage-400', 'bg-sage-300', 'bg-sage-200', 'bg-sage-100'];
  return (
    <div className="space-y-3">
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${COLORS[i] ?? 'bg-ink-300'}`} />
              <span className="text-ink-700 font-medium truncate">{r.categoryName}</span>
            </div>
            <span className="font-mono font-bold text-ink-900 tabular-nums flex-shrink-0 ml-2">
              {fmtMoney(r.revenue)}
            </span>
          </div>
          <div className="h-2 bg-canvas-subtle rounded-pill overflow-hidden">
            <div
              className={`h-full ${COLORS[i] ?? 'bg-sage-500'} rounded-pill transition-all duration-500 ease-out-expo`}
              style={{ width: `${(r.revenue / max) * 100}%` }}
            />
          </div>
          <div className="text-2xs text-ink-500 mt-0.5">
            {fmtInt(Math.round(r.unitsSold))} units · {fmtInt(r.lineCount)} lines
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// AlertCard — bottom-row watch tile
// ============================================================
function AlertCard({
  icon, title, value, description, tone, action,
}: {
  icon: ReactNode;
  title: string;
  value: string | number;
  description: string;
  tone: 'success' | 'warning' | 'danger' | 'default';
  action?: { label: string; to: string };
}) {
  const TONES = {
    success: { icon: 'bg-sage-100 text-sage-600 ring-sage-200',   bar: 'bg-sage-500',  value: 'text-sage-700' },
    warning: { icon: 'bg-amber-100 text-amber-700 ring-amber-200', bar: 'bg-amber-500', value: 'text-amber-800' },
    danger:  { icon: 'bg-red-100 text-danger ring-red-200',        bar: 'bg-danger',    value: 'text-danger' },
    default: { icon: 'bg-canvas-subtle text-ink-500 ring-ink-200', bar: 'bg-ink-300',   value: 'text-ink-900' },
  };
  const t = TONES[tone];

  return (
    <div className="card p-5 flex flex-col relative overflow-hidden">
      <div className={`absolute top-0 left-0 bottom-0 w-1 ${t.bar} rounded-l-xl`} />
      <div className="pl-3 flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ring-1 ring-inset ${t.icon}`}>
          {icon}
        </div>
        <div className="eyebrow text-ink-500">{title}</div>
      </div>
      <div className={`pl-3 text-3xl font-bold font-display tracking-tight ${t.value}`}>
        {typeof value === 'number' ? fmtInt(value) : value}
      </div>
      <div className="pl-3 text-sm text-ink-600 mt-2 flex-1">{description}</div>
      {action && (
        <Link
          to={action.to}
          className="pl-3 mt-4 text-sm font-semibold text-ink-900 hover:text-sage-700 inline-flex items-center gap-1 group"
        >
          {action.label}
          <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
        </Link>
      )}
    </div>
  );
}

// ============================================================
// Icons
// ============================================================
const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

function ChartIcon() { return <svg {...iconProps}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>; }
function CartIcon() { return <svg {...iconProps}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>; }
function AlertIcon() { return <svg {...iconProps} width="18" height="18"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }
function ClockIcon() { return <svg {...iconProps} width="18" height="18"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }
function WalletIcon() { return <svg {...iconProps} width="18" height="18"><path d="M20 12V8H6a2 2 0 01-2-2V6a2 2 0 012-2h12v4"/><path d="M4 6v12a2 2 0 002 2h14v-4"/><path d="M18 12a2 2 0 000 4h4v-4z"/></svg>; }
